import { useTags } from "@/features/modals/TagsModal/tags";
import { getMatchingTranslationforTags } from "@/features/modals/TagsModal/utils";
import { AmountFilterOption } from "@/features/projects/filter/constants";
import { SelectedFilterPill } from "@/features/projects/filter/selectedFilters/SelectedFilterPill";
import { formatAmountRange } from "@/utils/formatAmountRange";
import { getCurrencyCode } from "@/utils/getCurrency";
import styled from "@emotion/styled";
import {
  type OpportunitiesSearchFilters,
  type OpportunitiesSearchOptions,
  type Opportunity,
  ProjectFilterDefault,
} from "@repo/api";
import { Pill, Variables } from "@repo/skapa";
import { getLanguageLocal, t, useTranslation } from "@repo/translation";
import {
  localizeDateRange,
  translateClassification,
  translateConsentFilterStatus,
  translateConsentType,
  translateCustomerType,
  translateInstallationQuotationExists,
  translateInstallationQuotationStatus,
  translateInstallationType,
  translateLanguage,
  translateNbaType,
  translateOpportunityType,
  translateOrderMethod,
  translateOrderStatus,
  translateProjectStatus,
  translateServices,
  translateStage,
  useOrderedCustomerMeetingPoints,
  useOrderedDepartments,
  useOrderedServices,
  useSettings,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { type PayloadActionCreator } from "typesafe-actions";

import { DateFilterOption } from "../dateFilter/utils";
import { type ProjectFilterActions } from "../projectFilterReducer";
import { SORT_KEY_LABEL } from "../sortOptions";
import { hasActiveSort } from "../utils/hasActiveSort";

const Container = styled.div`
  max-width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${Variables.space75};
`;

const SelectedAmountPill = ({
  actions,
  amount,
  option,
}: {
  actions: ProjectFilterActions;
  amount?: number[];
  option: AmountFilterOption;
}) => {
  const { general } = useSettings();
  const currency = getCurrencyCode(general);
  const { market, language } = useSettings();
  const locale = getLanguageLocal(language, market);

  if (amount === undefined) {
    return null;
  }

  const amountConfig = {
    [AmountFilterOption.BUDGET]: {
      label: "label.budget",
      onClick: () => {
        actions.setBudget(ProjectFilterDefault.budgetFilter);
      },
    },
    [AmountFilterOption.VALUE]: {
      label: "label.value",
      onClick: () => {
        actions.setAmount(ProjectFilterDefault.amount);
      },
    },
  };

  const handleClick = amountConfig[option]?.onClick;

  return (
    <SelectedFilterPill
      label={`${t(amountConfig[option]?.label ?? "")}: ${formatAmountRange(
        amount,
        currency,
        locale,
        Boolean(general?.localization?.disable_decimals)
      )}`}
      onClick={handleClick}
    />
  );
};

const SelectedDatePill = ({
  actions,
  option,
  dates,
}: {
  actions: ProjectFilterActions;
  option: DateFilterOption;
  dates?: (Date | undefined)[];
}) => {
  const { dateFnsLocale } = useTranslation();

  if (dates?.length === 0) {
    return null;
  }

  const config = {
    [DateFilterOption.CREATED_DATE]: {
      label: t("label.created"),
      onClick: () => {
        actions.setCreatedDate(ProjectFilterDefault.createdDate ?? []);
      },
    },
    [DateFilterOption.UPDATED_DATE]: {
      label: t("label.updated"),
      onClick: () => {
        actions.setUpdatedDate(ProjectFilterDefault.updatedDate ?? []);
      },
    },
    [DateFilterOption.INSTALLATION_DATE]: {
      label: t("label.installation_date"),
      onClick: () => {
        actions.setInstallationDate(
          ProjectFilterDefault.installationDate ?? []
        );
      },
    },
    [DateFilterOption.EXPECTED_PURCHASE_DATE]: {
      label: t("label.expected_purchase_date"),
      onClick: () => {
        actions.setExpectedPurchaseDate(
          ProjectFilterDefault.expectedPurchaseDateFilter ?? []
        );
      },
    },
  };

  const labelText = config[option]?.label ?? "";
  const onClick = config[option]?.onClick;

  return (
    <SelectedFilterPill
      label={`${labelText} ${localizeDateRange(dates?.[0], dates?.[1], dateFnsLocale)}`}
      onClick={onClick}
    />
  );
};

const SelectedSearchPill = ({
  value,
  setValue,
}: {
  value?: string;
  setValue: PayloadActionCreator<"SET_TERM", string | undefined>;
}) => {
  if (value === undefined) {
    return null;
  }

  return (
    <SelectedFilterPill
      label={value}
      onClick={() => {
        setValue(undefined);
        trackAnalytics("cancel_filter");
      }}
    />
  );
};

interface SelectedFiltersProps {
  filters: OpportunitiesSearchFilters;
  actions: ProjectFilterActions;
  options: OpportunitiesSearchOptions;
  onResetSort: () => void;
}

export const SelectedFilters = ({
  filters,
  actions,
  options,
  onResetSort,
}: SelectedFiltersProps) => {
  const { data: storeList } = useOrderedCustomerMeetingPoints();
  const departments = useOrderedDepartments();
  const { data: tags } = useTags();
  const services = useOrderedServices();
  const { market } = useSettings();

  return (
    <Container>
      {hasActiveSort(options) && SORT_KEY_LABEL[options.sortKey] && (
        <SelectedFilterPill
          label={`${t("label.sort")}: ${t(SORT_KEY_LABEL[options.sortKey])} ${options.sortOrder === "asc" ? "↑" : "↓"}`}
          onClick={() => {
            onResetSort();
            trackAnalytics("clear_sort_filter");
          }}
        />
      )}
      <SelectedSearchPill value={filters.term} setValue={actions.setTerm} />
      {filters.classification?.map((id) => (
        <SelectedFilterPill
          key={`classification-${id}`}
          label={translateClassification(id)}
          onClick={() => {
            actions.setClassification(id);
            trackAnalytics("clear_classification_filter");
          }}
        />
      ))}
      {filters.status?.map((id) => (
        <SelectedFilterPill
          key={`status-${id}`}
          label={translateProjectStatus(
            id.toLocaleLowerCase().replace("_", "") as Opportunity["status"]
          )}
          onClick={() => {
            actions.setStatus(id);
            trackAnalytics("clear_status_filter");
          }}
        />
      ))}
      {filters.isNoActionPlanned && (
        <SelectedFilterPill
          key="no-action-planned"
          label={t("status.no_action_planned")}
          onClick={() => {
            actions.setIsNoActionPlanned(false);
            trackAnalytics("clear_no_action_planned_filter");
          }}
        />
      )}
      {filters.isColdProject && (
        <SelectedFilterPill
          key="cold-project"
          label={t("status.cold_project")}
          onClick={() => {
            actions.setIsColdProject(false);
            trackAnalytics("clear_cold_project_filter");
          }}
        />
      )}
      {filters.department?.map((id) => (
        <SelectedFilterPill
          key={`dep-${id}`}
          label={departments.find((b) => b[0] === id)?.[1] ?? id}
          onClick={() => {
            actions.setDepartment(id);
            trackAnalytics("clear_department_filter");
          }}
        />
      ))}
      {filters.businessUnit?.map((id) => (
        <SelectedFilterPill
          key={`bu-${id}`}
          label={storeList.find((b) => b[0] === id)?.[1] ?? id}
          onClick={() => {
            actions.setBusinessUnit(id);
            trackAnalytics("clear_business_unit_filter");
          }}
        />
      ))}
      {filters.customerType?.map((id) => (
        <SelectedFilterPill
          key={`cust-${id}`}
          label={translateCustomerType(id)}
          onClick={() => {
            actions.setCustomerType(id);
            trackAnalytics("clear_customer_type_filter");
          }}
        />
      ))}
      {filters.services?.map((id) => (
        <SelectedFilterPill
          key={`ser-${id}`}
          label={translateServices(
            services?.find((b) => String(b[0]) === String(id))?.[1]
          )}
          onClick={() => {
            actions.setServices(id);
            trackAnalytics("clear_services_filter");
          }}
        />
      ))}

      {filters.opportunityType?.map((id) => (
        <SelectedFilterPill
          key={`type-${id}`}
          label={translateOpportunityType(id)}
          onClick={() => {
            actions.setOpportunityType(id);
            trackAnalytics("clear_opportunity_type_filter");
          }}
        />
      ))}

      {filters.orderMethod?.map((id) => (
        <SelectedFilterPill
          key={`type-${id}`}
          label={translateOrderMethod(id)}
          onClick={() => {
            actions.setOrderMethod(id);
            trackAnalytics("clear_order_method_filter");
          }}
        />
      ))}

      <SelectedAmountPill
        actions={actions}
        amount={filters.amount}
        option={AmountFilterOption.VALUE}
      />
      <SelectedDatePill
        actions={actions}
        dates={filters.createdDate}
        option={DateFilterOption.CREATED_DATE}
      />
      <SelectedDatePill
        actions={actions}
        dates={filters.updatedDate}
        option={DateFilterOption.UPDATED_DATE}
      />

      {filters.followUpConsentFilter && (
        <SelectedFilterPill
          label={`${translateConsentType("FOLLOWUP_CONSENT")} - ${translateConsentFilterStatus(filters.followUpConsentFilter)}`}
          onClick={() => {
            actions.setFollowUpConsent(undefined);
            trackAnalytics("clear_follow_up_consent_filter");
          }}
        />
      )}
      {filters.promotionalConsentFilter && (
        <SelectedFilterPill
          label={`${translateConsentType("PROMOTIONAL_CONSENT")} - ${translateConsentFilterStatus(filters.promotionalConsentFilter)}`}
          onClick={() => {
            actions.setPromotionalConsent(undefined);
            trackAnalytics("clear_promotional_consent_filter");
          }}
        />
      )}
      {filters.tags?.map((item) => (
        <SelectedFilterPill
          key={`tags-${item}`}
          label={getMatchingTranslationforTags(item, tags || [], market)}
          onClick={() => {
            actions.setTags(item);
            trackAnalytics("clear_tag_filter");
          }}
        />
      ))}
      {filters.nbaTypes?.map((item) => (
        <SelectedFilterPill
          key={`nba-types-${item}`}
          label={translateNbaType(item)}
          onClick={() => {
            actions.setNbaTypes(item);
            trackAnalytics("clear_nba_filter");
          }}
        />
      ))}
      {filters.orderStatus?.map((item) => (
        <SelectedFilterPill
          key={`order-statuses-${item}`}
          label={translateOrderStatus(item)}
          onClick={() => {
            actions.setOrderStatus(item);
            trackAnalytics("clear_order_status_filter");
          }}
        />
      ))}
      {filters.customerLocales?.map((item) => (
        <SelectedFilterPill
          key={`preferred-languages-${item}`}
          label={translateLanguage(item)}
          onClick={() => {
            actions.setCustomerLocales(item);
            trackAnalytics("clear_preferred_language_filter");
          }}
        />
      ))}
      {filters.bokaServices?.map((item) => (
        <SelectedFilterPill
          key={`boka-services-${item}`}
          label={translateServices(item)}
          onClick={() => {
            actions.setBokaServices(item);
            trackAnalytics("clear_boka_services_filter");
          }}
        />
      ))}
      {filters.installationQuotationStatus?.map((item) => (
        <SelectedFilterPill
          key={`installation-quotation-status-${item}`}
          label={translateInstallationQuotationStatus(item)}
          onClick={() => {
            actions.setInstallationQuotationStatus(item);
            trackAnalytics("clear_installation_quotation_status_filter");
          }}
        />
      ))}
      {filters.installationQuotationExists !== undefined && (
        <SelectedFilterPill
          key="installation-quotation-exists"
          label={translateInstallationQuotationExists(
            filters.installationQuotationExists
          )}
          onClick={() => {
            actions.setInstallationQuotationExists(undefined);
            trackAnalytics("clear_installation_quotation_exist_filter");
          }}
        />
      )}

      {filters.workOrderStatus?.map((item) => (
        <SelectedFilterPill
          key={`service-work-order-status-${item}`}
          label={item}
          onClick={() => {
            actions.setWorkOrderStatus(item);
            trackAnalytics("clear_work_order_status_filter");
          }}
        />
      ))}
      {filters.workOrderType?.map((item) => (
        <SelectedFilterPill
          key={`service-work-order-work-type-${item}`}
          label={item}
          onClick={() => {
            actions.setWorkOrderType(item);
            trackAnalytics("clear_work_order_work_type_filter");
          }}
        />
      ))}
      {filters.installationType?.map((item) => (
        <SelectedFilterPill
          key={`installation-type-${item}`}
          label={`${t("label.installation_type")} - ${translateInstallationType(item)}`}
          onClick={() => {
            actions.setInstallationType(item);
            trackAnalytics("clear_installation_type_filter");
          }}
        />
      ))}
      {filters.stageFilter?.map((item) => (
        <SelectedFilterPill
          key={`stage-${item}`}
          label={translateStage(item)}
          onClick={() => {
            actions.setStageFilter(item);
            trackAnalytics("clear_stage_filter");
          }}
        />
      ))}
      {filters.waitingList !== undefined && (
        <SelectedFilterPill
          key="waiting-list"
          label={t("label.waiting_list")}
          onClick={() => {
            actions.setWaitingList(undefined);
          }}
        />
      )}
      <SelectedDatePill
        actions={actions}
        dates={filters.installationDate}
        option={DateFilterOption.INSTALLATION_DATE}
      />
      <SelectedDatePill
        actions={actions}
        dates={filters.expectedPurchaseDateFilter}
        option={DateFilterOption.EXPECTED_PURCHASE_DATE}
      />
      <SelectedAmountPill
        actions={actions}
        amount={filters.budgetFilter}
        option={AmountFilterOption.BUDGET}
      />
      <Pill
        size="small"
        label={t("action.clear_all")}
        onClick={() => {
          actions.clearAll();
          onResetSort();
          trackAnalytics("clear_all_filters");
        }}
      />
    </Container>
  );
};
